import { useCallback, useRef, useState } from 'react'

import { useAnalytics } from '@/context/AnalyticsContext'
import { EVENTS } from '@flows/configs/analytics/constants'

interface OtpCheckMutation {
  mutateAsync: (input: { email: string }) => Promise<{ available: boolean }>
}

export interface OtpVerifyResult {
  success: boolean
  couponDropped?: boolean
}

interface OtpVerifyMutation {
  isPending: boolean
  mutateAsync: (input: { email: string; otpCode: string }) => Promise<OtpVerifyResult>
}

interface UseOtpFlowOptions {
  checkEmailMutation: OtpCheckMutation
  verifyOtpMutation: OtpVerifyMutation
  onVerified: (result: OtpVerifyResult) => void
  /** i18n keys for the error messages so the hook stays product-agnostic. */
  errorMessages: { invalidCode: string; resendError: string }
  /** Step name attached to ErrorMessageDuplicateEmail tracking. */
  currentStepName: string
}

/**
 * State + handlers for the "this email already has an account, send OTP"
 * detour shared by all checkout flows. Returns:
 * - `modalVisible` / `setModalVisible`: drives the modal open/close
 * - `email`: which address the OTP went to
 * - `code` / `setCode`: input field state
 * - `error`: localized error string or ''
 * - `resendDisabled`: throttles "send code again"
 * - `runEmailCheck(email)`: call on form submit; returns true if OTP is needed
 *   and false (or void) if the user can proceed normally
 * - `handleConfirm()`: verifies the code, calls `onVerified` on success
 * - `handleResend()`: retriggers the email check
 */
export const useOtpFlow = (options: UseOtpFlowOptions) => {
  const { track } = useAnalytics()

  const [modalVisible, setModalVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [resendDisabled, setResendDisabled] = useState(false)

  const onVerifiedRef = useRef(options.onVerified)
  onVerifiedRef.current = options.onVerified

  const runEmailCheck = useCallback(
    async (emailInput: string): Promise<boolean> => {
      try {
        const result = await options.checkEmailMutation.mutateAsync({ email: emailInput })
        if (!result.available) {
          track(EVENTS.ErrorMessageDuplicateEmail, {
            currentStepName: options.currentStepName,
            email: emailInput,
          })
          setEmail(emailInput)
          setModalVisible(true)
          return true
        }
      } catch {
        // If the check fails, fall through and let the caller proceed.
      }
      return false
    },
    [options.checkEmailMutation, options.currentStepName, track]
  )

  const requestCode = useCallback((emailInput: string) => {
    setEmail(emailInput)
    setModalVisible(true)
  }, [])

  const handleConfirm = useCallback(async () => {
    setError('')
    try {
      const result = await options.verifyOtpMutation.mutateAsync({ email, otpCode: code })
      track(EVENTS.ValidOtpCode)
      setModalVisible(false)
      setCode('')
      onVerifiedRef.current(result)
    } catch {
      track(EVENTS.InvalidOtpCode)
      setError(options.errorMessages.invalidCode)
    }
  }, [code, email, options.errorMessages.invalidCode, options.verifyOtpMutation, track])

  const handleResend = useCallback(async () => {
    setResendDisabled(true)
    setError('')
    try {
      await options.checkEmailMutation.mutateAsync({ email })
    } catch {
      setError(options.errorMessages.resendError)
    }
    setTimeout(() => setResendDisabled(false), 6000)
  }, [email, options.checkEmailMutation, options.errorMessages.resendError])

  const reset = useCallback(() => {
    setModalVisible(false)
    setCode('')
    setError('')
  }, [])

  return {
    modalVisible,
    setModalVisible,
    email,
    code,
    setCode,
    error,
    resendDisabled,
    isVerifying: options.verifyOtpMutation.isPending,
    runEmailCheck,
    requestCode,
    handleConfirm,
    handleResend,
    reset,
  }
}
